import { blocksSchema } from '@/entities/chat-message/schema';
import type {
  Block,
  ChatMessage,
  ChatMessageInput,
  ToolCall,
} from '@/entities/chat-message/types';
import { agentClient } from './agent';

// Must stay in sync with CHECK_IN_INSIGHTS_ACTION in supabase/functions/ai-agent/handler.ts
const CHECK_IN_INSIGHTS_ACTION = 'extractCheckInInsights';

export interface AiResponse {
  chatMessageInput: ChatMessageInput;
  toolCalls: ToolCall[];
}

interface MessageInput {
  conversationId: string;
  messages: ChatMessage[];
  context?: object;
}

export interface JournalEntry {
  promptKey: string;
  promptText: string;
  response: string;
}

interface ExtractCheckInInsightsInput {
  sessionId: string;
  journalEntries: JournalEntry[];
  currentInsights: Block[];
  context: { user: { currentDateTime: string } };
}

function serializeBlocks(blocks: Block[]): string {
  return JSON.stringify(blocks);
}

function parseBlocks(message: string): Block[] {
  const parsed: unknown = JSON.parse(message);
  return blocksSchema.parse(parsed);
}

function messageToWireFormat(msg: ChatMessage): {
  role: string;
  content: string;
} {
  return { role: msg.role, content: serializeBlocks(msg.blocks) };
}

export const aiService = {
  sendMessage: async (sendInput: MessageInput): Promise<AiResponse> => {
    const wireMessages = sendInput.messages.map(messageToWireFormat);

    const response = await agentClient.sendToAgent({
      sessionId: sendInput.conversationId,
      messages: wireMessages,
      context: sendInput.context ?? {},
    });

    return {
      chatMessageInput: {
        conversationId: sendInput.conversationId,
        blocks: parseBlocks(response.message),
        role: 'assistant',
      },
      toolCalls: response.toolCalls,
    };
  },

  extractCheckInInsights: async (
    input: ExtractCheckInInsightsInput,
  ): Promise<Block[]> => {
    const response = await agentClient.sendToAgent({
      action: CHECK_IN_INSIGHTS_ACTION,
      sessionId: input.sessionId,
      journalEntries: input.journalEntries,
      currentInsights: input.currentInsights,
      context: input.context,
    });

    return parseBlocks(response.message);
  },
};
